import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterIcon from "@mui/icons-material/FilterAlt";
import ApiEndpoints from "../network/ApiEndPoints";
import { get } from "../network/ApiController";
import { apiErrorToast } from "../utils/ToastUtil";

const MerchantList = () => {
  const [merchants, setMerchants] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [pan, setPan] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  const getMerchants = () => {
    setIsLoading(true);
    let query = "";
    if (outletId) query += `outletId=${outletId}&`;
    if (pan) query += `pan=${pan}&`;
    if (mobileNo) query += `mobile=${mobileNo}&`;
    query += "page=1&paginate=10";

    get(
      ApiEndpoints.GET_MERCHANTS,
      `${query}`,
      setIsLoading,
      (res) => {
        const apiData = res?.data?.data;
        setMerchants(apiData);
        setHasSearched(true);
        setIsLoading(false);
      },
      (err) => {
        apiErrorToast(err);
        setIsLoading(false);
        setHasSearched(true);
      }
    );
  };

  const resetSearch = () => {
    setOutletId("");
    setPan("");
    setMobileNo("");
    setMerchants([]);
    setHasSearched(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Merchant List
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={resetSearch} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

   <Grid
  container
  spacing={1}
  alignItems="center"
  sx={{
    mb: 1,
    p: 2,
    borderRadius: 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  }}
>
 
  <Grid item xs={12} sm={6} md={3}>
    <TextField
      fullWidth
      label="Outlet ID"
      variant="outlined"
      value={outletId}
      onChange={(e) => setOutletId(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FilterIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
    />
  </Grid>


  <Grid item xs={12} sm={6} md={3}>
    <TextField
      fullWidth
      label="PAN"
      variant="outlined"
      value={pan}
      onChange={(e) => setPan(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FilterIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
    />
  </Grid>


  <Grid item xs={12} sm={6} md={3}>
    <TextField
      fullWidth
      label="Mobile No"
      variant="outlined"
      value={mobileNo}
      onChange={(e) => setMobileNo(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FilterIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
    />
  </Grid>

  {/* Action Buttons */}
  <Grid item xs={12} sm={6} md={3}>
    <Box sx={{ display: "flex", gap: 1, height: "100%" }}>
      <Button
        fullWidth
        variant="contained"
        onClick={getMerchants}
        disabled={!outletId && !pan && !mobileNo}
        startIcon={<SearchIcon />}
        sx={{
          textTransform: "none",
          py: 0.8,
          px: 1.5,
          borderRadius: 1,
          fontSize: 14,
          height: "100%",
          minHeight: "40px" // Match TextField height
        }}
      >
        {isLoading ? "Searching..." : "Search"}
      </Button>

      <Button
        variant="outlined"
        onClick={resetSearch}
        startIcon={<ClearIcon fontSize="small" />}
        sx={{
          textTransform: "none",
          borderRadius: 1,
          fontSize: 12,
          height: "100%",
          minHeight: "40px", // Match TextField height
          px: 1.5,
          minWidth: "auto"
        }}
      >
        Clear
      </Button>
    </Box>
  </Grid>
</Grid>

      {!hasSearched && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            textAlign: "center",
            bgcolor: "background.default",
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Enter any search criteria and click <strong>'Search'</strong> to
            find merchants.
          </Typography>
        </Box>
      )}

      {hasSearched && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Search Results
            </Typography>
            <Chip
              label={`${merchants.length} records found`}
              size="small"
              color={merchants.length > 0 ? "primary" : "default"}
              variant="outlined"
            />
          </Box>

          {merchants?.length > 0 ? (
            <TableContainer
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{
                    bgcolor:
                      theme.palette.mode === "light"
                        ? "grey.100"
                        : "background.default",
                  }}
                >
                  <TableRow>
                    {[
                      "Outlet ID",
                      "Name",
                      "Mobile No",
                      "PAN",
                      "Latitude",
                      "Longitude",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchants.length > 0 &&
                    merchants?.map((merchant, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{merchant?.outletId}</TableCell>
                        <TableCell>{merchant?.name}</TableCell>
                        <TableCell>{merchant?.mobile}</TableCell>
                        <TableCell>{merchant?.pan}</TableCell>
                        <TableCell>{merchant?.Latitude}</TableCell>
                        <TableCell>{merchant?.Longitude}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                mt: 3,
                p: 4,
                textAlign: "center",
                bgcolor: "background.paper",
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <SearchIcon fontSize="large" color="disabled" />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No merchants found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MerchantList;
